import React, { useState } from 'react';
import { Wrench, ChevronDown, ChevronRight } from 'lucide-react';
import type { GlassBoxItem } from '../../../hooks/useGlassBoxEvents';

interface Props {
  item: GlassBoxItem;
}

export function ToolCallEvent({ item }: Props) {
  const [expanded, setExpanded] = useState(false);
  const { data } = item;
  // The DO broadcasts one frame per batch (tool_names[], total_duration_ms, error_count);
  // backfilled rows are normalized to the same shape. The singular keys are the older shape.
  const toolNames = Array.isArray(data.tool_names)
    ? data.tool_names.filter((n): n is string => typeof n === 'string')
    : [];
  const toolName = toolNames.length > 0
    ? toolNames.join(', ')
    : typeof data.tool_name === 'string' ? data.tool_name : 'unknown';
  const durationMs = typeof data.total_duration_ms === 'number'
    ? data.total_duration_ms
    : typeof data.duration_ms === 'number' ? data.duration_ms : null;
  const status = typeof data.status === 'string'
    ? data.status
    : typeof data.error_count === 'number' && typeof data.success_count === 'number'
      ? (data.error_count > 0 ? 'error' : 'success')
      : null;
  const isComplete = item.event.includes('complete');
  const isError = status === 'error' || status === 'timeout';
  const isPending = status === 'pending_approval';

  const extraKeys = Object.keys(data).filter(
    k => !['tool_name', 'tool_names', 'duration_ms', 'total_duration_ms', 'status'].includes(k)
  );

  return (
    <div className="py-1.5 px-3 hover:bg-surface-800/40 rounded group">
      <div className="flex items-center gap-2">
        <Wrench className={`w-3.5 h-3.5 flex-shrink-0 ${isError ? 'text-error-400' : 'text-amber-400'}`} />
        <button
          onClick={() => setExpanded(e => !e)}
          className="flex-1 flex items-center gap-2 text-left min-w-0"
        >
          <span className={`text-xs font-medium ${isError ? 'text-error-300' : 'text-amber-300'}`}>
            {isComplete ? 'Tool complete' : 'Tool call'}
          </span>
          <span className="text-[10px] text-text-muted font-mono truncate">{toolName}</span>
          {durationMs !== null && (
            <span className="text-[10px] text-text-muted">{durationMs}ms</span>
          )}
          {status && (
            <span className={`text-[10px] px-1 rounded ${isError ? 'bg-error-500/20 text-error-400' : isPending ? 'bg-surface-700 text-text-muted' : 'bg-success-500/20 text-success-400'}`}>
              {status}
            </span>
          )}
          {extraKeys.length > 0 && (
            expanded
              ? <ChevronDown className="w-3 h-3 text-text-muted ml-auto flex-shrink-0" />
              : <ChevronRight className="w-3 h-3 text-text-muted ml-auto flex-shrink-0" />
          )}
        </button>
        <div className="flex-shrink-0 text-[10px] text-text-muted opacity-0 group-hover:opacity-100 transition-opacity">
          {new Date(item.receivedAt).toLocaleTimeString([], { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' })}
        </div>
      </div>
      {expanded && extraKeys.length > 0 && (
        <div className="mt-1.5 ml-5 p-2 rounded bg-surface-900/60 border border-surface-700 text-[10px] font-mono text-text-secondary space-y-0.5">
          {extraKeys.map(k => (
            <div key={k} className="truncate">
              <span className="text-text-muted">{k}: </span>
              {typeof data[k] === 'object' ? JSON.stringify(data[k]) : String(data[k])}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
